import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router';
import { api } from '../../lib/api';
import type { VisitLink } from '../../lib/types';
import { MODE_LABELS, PAYMENT_LABELS, STATUS_LABELS, formatDateTime, formatPrice } from '../../lib/format';
import { Alert, Dialog, ErrorMessage, Loading, PageTitle } from '../../components/ui';
import { Pii } from '../../components/Pii';

/**
 * Visit booked on the website without an account (/wizyta#token). The token stays in the URL fragment – it is
 * never sent to the server in a URL (logs) – and travels in request bodies only.
 */
export function VisitPage() {
  const { hash } = useLocation();
  const token = hash.replace(/^#/, '');
  const client = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const visit = useQuery({
    queryKey: ['visit-link', token],
    queryFn: () => api.post<VisitLink>('/api/v1/visit/details', { token }),
    enabled: token.length > 0,
    retry: false,
  });
  const pay = useMutation({
    mutationFn: () => api.post<{ redirectUrl: string }>('/api/v1/visit/pay', { token }),
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  });
  const cancel = useMutation({
    mutationFn: () => api.post<VisitLink>('/api/v1/visit/cancel', { token }),
    onSuccess: (data) => {
      setCancelOpen(false);
      client.setQueryData(['visit-link', token], data);
    },
  });

  if (!token) {
    return (
      <>
        <PageTitle>Twoja wizyta</PageTitle>
        <Alert>Link jest niekompletny. Otwórz go ponownie z wiadomości SMS lub e-mail.</Alert>
      </>
    );
  }
  if (visit.error) {
    return (
      <>
        <PageTitle>Twoja wizyta</PageTitle>
        <ErrorMessage error={visit.error} />
      </>
    );
  }
  if (!visit.data) return <Loading />;

  const v = visit.data;
  const cancelled = v.status === 'Cancelled';
  return (
    <>
      <PageTitle>Twoja wizyta</PageTitle>
      {cancel.isSuccess && (
        <Alert kind="success">
          Wizyta została odwołana.{v.paymentStatus === 'Paid' || v.paymentStatus === 'Refunded' ? ' Płatność zwrócimy w ciągu kilku dni roboczych.' : ''}
        </Alert>
      )}
      {v.canPay && v.paymentDueAt && (
        <Alert kind="warning">Opłać wizytę do {formatDateTime(v.paymentDueAt)} – nieopłacona wizyta zostanie automatycznie odwołana.</Alert>
      )}
      <dl className="summary card">
        <dt>Termin</dt>
        <dd>{formatDateTime(v.startsAt)}</dd>
        <dt>Terapeuta</dt>
        <dd>
          <Pii>{v.therapistName}</Pii>
        </dd>
        <dt>Rodzaj wizyty</dt>
        <dd>
          {v.serviceName} · {MODE_LABELS[v.mode]}
        </dd>
        {v.locationName && (
          <>
            <dt>Miejsce</dt>
            <dd>
              {v.locationName}
              {v.locationAddress && `, ${v.locationAddress}`}
              {v.roomName && ` · ${v.roomName}`}
            </dd>
          </>
        )}
        <dt>Status</dt>
        <dd>{STATUS_LABELS[v.status]}</dd>
        {v.paymentStatus !== 'NotRequired' && (
          <>
            <dt>Płatność</dt>
            <dd>
              {PAYMENT_LABELS[v.paymentStatus]}
              {v.price != null && ` · ${formatPrice(v.price)}`}
            </dd>
          </>
        )}
      </dl>
      {v.meetingUrl && (
        <p>
          <a className="button" href={v.meetingUrl} target="_blank" rel="noopener noreferrer">
            Dołącz do wizyty online (Google Meet)
          </a>
          <br />
          <span className="muted">Nie potrzebujesz konta Google: wpisz imię i kliknij „Poproś o dołączenie” – terapeuta Cię wpuści.</span>
        </p>
      )}
      <ErrorMessage error={pay.error} />
      <div className="button-row">
        {v.canPay && (
          <button type="button" onClick={() => pay.mutate()} disabled={pay.isPending}>
            Zapłać{v.price != null && ` ${formatPrice(v.price)}`}
          </button>
        )}
        {v.canCancel && (
          <button type="button" className="danger" onClick={() => setCancelOpen(true)}>
            Odwołaj wizytę
          </button>
        )}
      </div>
      {!cancelled && !v.canCancel && <p className="muted">Wizytę można odwołać najpóźniej 24 godziny przed jej rozpoczęciem. Później skontaktuj się z placówką.</p>}
      <Dialog open={cancelOpen} title="Odwołać wizytę?" onClose={() => setCancelOpen(false)}>
        <p>{formatDateTime(v.startsAt)}</p>
        {v.paymentStatus === 'Paid' && <p>Opłacona kwota zostanie zwrócona.</p>}
        <ErrorMessage error={cancel.error} />
        <div className="button-row">
          <button type="button" className="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
            Odwołaj wizytę
          </button>
          <button type="button" className="secondary" onClick={() => setCancelOpen(false)}>
            Zostaw
          </button>
        </div>
      </Dialog>
    </>
  );
}

type PaymentResult = 'pending' | 'completed' | 'canceled' | 'refunded';

/** Seconds after a successful payment before the payer is sent back to the website they booked on. */
export const RETURN_DELAY_SECONDS = 5;

/** Only http(s) addresses – the API checks the site against the allowed websites already. */
function safeReturnUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * Page the payment provider returns to (/platnosc?id=…); the provider adds error=… when the payment failed.
 * A visit booked on the website sends the payer back to that page (e.g. the therapist's page) after paying.
 */
export function PaymentReturnPage() {
  const [params] = useSearchParams();
  const id = params.get('id') ?? '';
  const failed = params.has('error');
  const [attempts, setAttempts] = useState(0);
  const status = useQuery({
    queryKey: ['payment-status', id, attempts],
    queryFn: () => api.get<{ status: PaymentResult; returnUrl?: string | null }>(`/api/v1/payments/${encodeURIComponent(id)}/status`),
    enabled: /^[0-9a-f-]{36}$/i.test(id) && (!failed || attempts === 0),
    retry: false,
  });

  // The provider's notification may arrive a moment after the redirect: check again for about a minute.
  const pending = !failed && status.data?.status === 'pending';
  useEffect(() => {
    if (!pending || attempts >= 20) return;
    const timer = setTimeout(() => setAttempts((n) => n + 1), 3000);
    return () => clearTimeout(timer);
  }, [pending, attempts]);

  const result: PaymentResult | 'failed' | undefined = failed ? 'failed' : status.data?.status;
  const returnUrl = safeReturnUrl(status.data?.returnUrl);
  const goBack = result === 'completed' && returnUrl !== null;
  useEffect(() => {
    if (!goBack) return;
    const timer = setTimeout(() => window.location.assign(returnUrl), RETURN_DELAY_SECONDS * 1000);
    return () => clearTimeout(timer);
  }, [goBack, returnUrl]);

  return (
    <>
      <PageTitle>Płatność</PageTitle>
      {result === 'completed' && (
        <Alert kind="success">Dziękujemy! Płatność została przyjęta, a wizyta jest opłacona. Potwierdzenie wysłaliśmy SMS-em lub e-mailem.</Alert>
      )}
      {result === 'pending' &&
        (attempts < 20 ? (
          <>
            <Alert kind="info">Czekamy na potwierdzenie płatności od operatora…</Alert>
            <Loading />
          </>
        ) : (
          <Alert kind="info">Płatność jest jeszcze przetwarzana. Gdy ją otrzymamy, wyślemy potwierdzenie SMS-em lub e-mailem.</Alert>
        ))}
      {(result === 'failed' || result === 'canceled') && (
        <Alert kind="warning">Płatność nie została zrealizowana. Możesz spróbować ponownie, korzystając z linku w wiadomości SMS lub e-mail.</Alert>
      )}
      {result === 'refunded' && <Alert kind="info">Ta płatność została zwrócona.</Alert>}
      {!failed && !id && <Alert>Brak informacji o płatności.</Alert>}
      {goBack && <p>Za chwilę wrócisz na stronę, na której umówiono wizytę.</p>}
      {returnUrl && result !== undefined && result !== 'pending' && (
        <p>
          <a className="button" href={returnUrl}>
            Wróć na stronę
          </a>
        </p>
      )}
      <ErrorMessage error={status.error} />
    </>
  );
}
